import { Box, Card, Container, Stack, Typography } from "@mui/material";
import ButtonApp from "components/ButtonApp";
import Loading from "components/Loading";
import Notification from "components/Notification";
import Steps from "components/Steps";
import { useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ButtonSubmitForm from "./ButtonSubmitForm";
import useSteps from "./hooks/steps";
import useCreateProspect from "./hooks/useCreateProspect";

export default function FormSteps() {
  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      companyStatus: "",
      legalStatus: "",
      hasAccountant: "",
      accountantService: "",
      accountantType: "",
      sector: "",
      annualRevenue: "",
      employeeCount: "",
    },
  });
  const { handleSubmit, trigger } = methods;
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const steps = useSteps();
  const [invalidStep, setInvalidStep] = useState(false);

  const { createPropspect, loading } = useCreateProspect();

  const nextStep = async () => {
    const stepFields = steps[currentStep].fields;

    if (stepFields) {
      const fields = stepFields.map(field => field as keyof FormControlType);
      const isValid = await trigger(fields);
      if (isValid) {
        setInvalidStep(false);
        setCurrentStep(currentStep + 1);
      } else {
        setInvalidStep(true);
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prevStep => {
      return prevStep - 1;
    });
    setInvalidStep(false);
  };

  const onFinish = async (values: any, token: string) => {
    const isCompanyString = values.company && typeof values.company === "string";

    const formattedAddress =
      isCompanyString && values.company?.address
        ? ["addressNr", "address", "address2", "postalCode", "township"]
            .map(key => values.company?.address?.[key])
            .filter(Boolean)
            .join(" ")
        : undefined;

    const customInputValues = {
      companyName: values.company?.value || values.company || undefined,
      address: formattedAddress,
      siret: values.company?.vatNumber || undefined,
      activityCode: values.company?.businessActivity || undefined,
      legalStatus: values.legalStatus || values.company?.legalStatus || undefined,
      otherService:
        values.accountantService === "other" ? values.accountantServiceOther : undefined,
      otherSector: values.sector === "other" ? values.sectorOther : undefined,
      hasAccountant: values.hasAccounting === true,
    };

    delete values.accountantServiceOther;
    delete values.sectorOther;
    delete values.company;

    const { data } = await createPropspect({
      variables: {
        input: {
          ...values,
          ...customInputValues,
          recaptchaToken: token,
        },
      },
    });

    if (data?.createProspect.success) {
      setCurrentStep(currentStep + 1);
    } else {
      setDisplayErrorMessage(true);
    }
  };

  const hideNotification = () => {
    setDisplayErrorMessage(false);
  };

  const handleFormSubmit = (token: string) => {
    handleSubmit(data => {
      onFinish(data, token);
    })();
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={`${process.env.REACT_APP_GOOGLE_SITE_KEY}`}>
      <Loading loading={loading} />
      <Container className="container">
        <Card variant="outlined" sx={{ borderRadius: "12px", padding: "22px" }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1.35rem", md: "1.5rem", lg: "2rem" },
                textAlign: "center",
              }}
            >
              {t("Confiez votre comptabilité à un prestataire de confiance")}
            </Typography>
            <Typography textAlign="center" fontSize={{ xs: "1rem", md: "1.40rem" }}>
              {t("Remplissez le formulaire pour comparer jusqu'à 4 devis")}
            </Typography>

            <Stack spacing={4} justifyContent="center" margin="unset" width="100%">
              <Steps steps={steps} activeStep={currentStep} />
              <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column", gap: 2, justifyContent: "center" }}
              >
                <Stack spacing={2} paddingX={{ sx: 1, md: 10 }}>
                  <Typography variant="h6" color={invalidStep ? "error" : "textPrimary"}>
                    {steps[currentStep].title}
                  </Typography>
                  <FormProvider {...methods}>{steps[currentStep].content}</FormProvider>
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                className="stepsButtonsContainer"
              >
                {currentStep > 0 && currentStep < steps.length - 1 && (
                  <ButtonApp primary={false} label={t("Previous")} onclick={() => prevStep()} />
                )}
                {currentStep < steps.length - 2 && (
                  <ButtonApp primary label={t("Next")} onclick={() => nextStep()} />
                )}
                {currentStep === steps.length - 2 && (
                  <ButtonSubmitForm submitForm={handleFormSubmit} />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Container>
      <Notification
        message={t("Erreur lors de l'ouverture du document, veuillez contacter l'administrateur.")}
        type="error"
        side="right"
        open={displayErrorMessage}
        onClose={hideNotification}
      />
    </GoogleReCaptchaProvider>
  );
}
